// src/services/documentosService.js
import { apiClient } from './api';
import { store } from '../redux/store';

/**
 * Servicio para manejar documentos del RCV
 */
export const documentosService = {
  /**
   * Obtener documentos de un periodo para una empresa
   * @param {string} rutEmpresa - RUT de la empresa
   * @param {string} periodo - Periodo en formato YYYYMM
   * @returns {Promise<Array>} - Promesa con la lista de documentos
   */
  async getDocumentosPeriodo(rutEmpresa, periodo, tipoOperacion = 'COMPRA') {
    if (tipoOperacion === 'VENTA') {
      return apiClient.get(`/rcv/ventas/documentos?rut_empresa=${rutEmpresa}&periodo=${periodo}`);
    }
    return apiClient.get(`/rcv/documentos?rut_empresa=${rutEmpresa}&periodo=${periodo}`);
  },

  /**
   * Obtener documentos de una carga específica
   * @param {number} cargaId - ID de la carga
   * @returns {Promise<Array>} - Promesa con la lista de documentos
   */
  async getDocumentosCarga(cargaId) {
    const { tokenSII } = store.getState().auth;
    const headers = tokenSII ? { 'X-SII-Token': tokenSII } : {};
    
    return apiClient.get(
      `/rcv/documentos/${cargaId}`,
      { headers }
    );
  },

  /**
   * Cargar documentos del RCV seleccionados por el usuario
   * @param {Object} params - Parámetros y documentos para la carga
   * @returns {Promise<Object>} - Promesa con la información de la carga
   */
  async cargarDocumentos(params) {
    if (!params.documentos_seleccionados || params.documentos_seleccionados.length === 0) {
      throw new Error("No hay documentos seleccionados para cargar");
    }

    const tipoOperacion = params.tipo_operacion || 'COMPRA';
    const requestData = {
      rut_empresa: params.rut_empresa,
      dv_empresa: params.dv_empresa,
      periodo: params.periodo,
      tipo_operacion: tipoOperacion,
      estado_contab: params.estado_contab || 'REGISTRO',
      documentos_seleccionados: params.documentos_seleccionados
    };

    if (tipoOperacion === 'VENTA') {
      return apiClient.post('/rcv/ventas/cargar', requestData);
    }

    return apiClient.post('/rcv/cargar', requestData);
  },

  /**
   * Obtener lista de cargas realizadas
   * @param {string} rutEmpresa - RUT de la empresa
   * @param {string} periodo - Periodo opcional para filtrar
   * @returns {Promise<Array>} - Promesa con la lista de cargas
   */
  async getCargas(rutEmpresa, periodo = null) {
    //const { tokenSII } = store.getState().auth;
    
    let endpoint = `/rcv/cargas?rut_empresa=${rutEmpresa}`;
    if (periodo) {
      endpoint += `&periodo=${periodo}`;
    }
    
    return apiClient.get(endpoint);
  },

  /**
   * Cargar datos desde un archivo CSV
   * @param {File} file - Archivo CSV a procesar
   * @returns {Promise<Array>} - Promesa con los datos procesados
   */
  async procesarCsvRcv(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const csvData = event.target.result;
        try {
          // Aquí puedes implementar la lógica para procesar el CSV
          // En este caso, estamos delegando el procesamiento al componente CsvUploadTab
          // usando la librería Papa Parse
          resolve(csvData);
        } catch (err) {
          reject(new Error(`Error al procesar CSV: ${err.message}`));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error al leer el archivo CSV'));
      };
      
      reader.readAsText(file);
    });
  },

  /**
   * Cargar documentos CSV a la base de datos
   * @param {Object} params - Parámetros para la carga
   * @returns {Promise<Object>} - Promesa con la información de la carga
   */
  async cargarDocumentosCsv(params) {
    // const { tokenSII } = store.getState().auth;
    
    // La estructura de params debería ser la misma que para cargarDocumentos
    // Solo se añade un flag para indicar que vienen de CSV
    const requestData = {
      ...params,
      origen: 'CSV'
    };
    
    return apiClient.post('/rcv/cargar', requestData);
  },
};